/**
 * The Merge jar as a cartoon glass jar, matching the Fall catcher flask
 * (render/toon/props/flask-art.js): pale translucent glass with a cel shade on the lower right,
 * a bold lit band down the left and a thin one on the right, a thick ink outline round the U,
 * plain inked wall tops, a soft floor shade and a few bubbles.
 *
 * The inner silhouette is the physics one and must not move: the open-top U from the inner
 * wall faces (neckLeft..neckRight, T..bodyBottom) with the tier-0 radius on the bottom corners,
 * traced by `traceJarUPath` exactly as before. The glass wall is drawn outside it (where the
 * physics walls are), so a body resting on the floor or a wall touches the glass, never sinks
 * into it.
 *
 * Three layers, baked once per size into canvases at the renderer density:
 *   back  — under the creatures: glass tint, shade crescent, floor shade, bubbles;
 *   front — over the creatures: the glass walls, ink and the highlight bands;
 *   rim   — the front half of the mouth, in its own thin strip so the game can fade it out
 *           around the creature being aimed at the mouth (render/shake-jar-visual.js).
 * Coordinates below are jar-local: (0, 0) is the inner top-left corner.
 */
import { INK } from "../palette.js";
import { TAU, hash, inkStyle } from "../toon-kit.js";
import { traceJarUPath } from "./jar-path.js";
import { markTextureNoMipmaps, releaseCanvasTexture } from "./texture-hints.js";

/** Glass tones for the night sky (the jar only shows in the Shake stage). */
export var JAR_GLASS = {
  tint: "rgba(150, 212, 246, 0.13)",
  crescent: "rgba(44, 96, 168, 0.2)",
  floor: "rgba(22, 52, 112, 0.3)",
  wall: "rgba(176, 228, 250, 0.6)",
  wallLight: "rgba(228, 248, 255, 0.82)",
  wallShade: "rgba(104, 170, 222, 0.66)",
  innerEdge: "rgba(23, 42, 58, 0.5)",
  stripe: "rgba(255, 255, 255, 0.9)",
  band: "rgba(255, 255, 255, 0.24)",
  bandThin: "rgba(255, 255, 255, 0.2)",
  bubble: "rgba(255, 255, 255, 0.55)",
  rimBack: "rgba(190, 232, 252, 0.42)",
  rimFront: "rgba(200, 238, 255, 0.78)",
  rimInk: "rgba(23, 42, 58, 0.55)",
};

function clamp(v, a, b) {
  return v < a ? a : v > b ? b : v;
}

/**
 * Wall, ink and lip sizes for a jar (px).
 * @param {number} w inner width
 * @param {number} h inner height
 * @param {number} r inner corner radius (tier-0 body radius)
 * @param {number} [roomBelow] px between the floor and the bottom of the clip (panel bottom):
 *   the bottom wall is thinned to fit so its ink is never cut off
 */
export function jarArtMetrics(w, h, r, roomBelow) {
  var wall = clamp(w * 0.034, 8, 18);
  var ink = clamp(wall * 0.3, 2.6, 5);
  var base = wall;
  if (roomBelow != null && isFinite(roomBelow)) base = clamp(roomBelow - ink * 0.5 - 0.75, 2, wall);
  var lipW = wall * 2.1;
  var lipH = wall * 1.05 + ink;
  var lipTop = -lipH * 0.55;
  var padX = wall + (lipW - wall) / 2 + ink;
  var sparkle = clamp(wall * 0.75, 5, 12);
  // Depth of the round mouth (half height of its ellipse): a shallow cylinder seen from a
  // little above, like the catcher flask's lip.
  var mouth = clamp(w * 0.03, 5, 16);
  return {
    sparkle: sparkle,
    mouth: mouth,
    w: w,
    h: h,
    r: Math.max(0, r || 0),
    wall: wall,
    base: base,
    ink: ink,
    lipW: lipW,
    lipH: lipH,
    lipTop: lipTop,
    // Canvas box relative to the inner top-left.
    left: -Math.ceil(padX + 1),
    top: -Math.ceil(Math.max(-lipTop + Math.max(ink, sparkle), mouth + wall * 0.3 + ink) + 1),
    right: Math.ceil(w + padX + 1),
    bottom: Math.ceil(h + base + ink + 1),
  };
}

function innerU(c, M, closeTop) {
  traceJarUPath(c, 0, 0, M.w, M.h, M.r, closeTop);
}

function outerU(c, M, closeTop) {
  traceJarUPath(c, -M.wall, 0, M.w + M.wall * 2, M.h + M.base, M.r + (M.wall + M.base) / 2, closeTop);
}

/** The glass wall band: outer U with the inner U cut out (even-odd). */
function wallBand(c, M) {
  c.beginPath();
  outerU(c, M, true);
  innerU(c, M, true);
}

function stripe(c, x0, y0, x1, y1, width, color) {
  if (!(width > 0)) return;
  c.beginPath();
  c.moveTo(x0, y0);
  c.lineTo(x1, y1);
  c.lineCap = "round";
  c.lineWidth = width;
  c.strokeStyle = color;
  c.stroke();
}

function bubble(c, x, y, r) {
  c.beginPath();
  c.arc(x, y, r, 0, TAU);
  c.fillStyle = "rgba(255,255,255,0.08)";
  c.fill();
  c.lineWidth = Math.max(1, r * 0.28);
  c.strokeStyle = JAR_GLASS.bubble;
  c.stroke();
  c.beginPath();
  c.arc(x - r * 0.35, y - r * 0.35, Math.max(0.6, r * 0.26), 0, TAU);
  c.fillStyle = "rgba(255,255,255,0.8)";
  c.fill();
}

/**
 * Under the creatures: glass tint, shade crescent (right wall and floor), floor shade, bubbles.
 * @param {CanvasRenderingContext2D} c jar-local transform
 * @param {ReturnType<typeof jarArtMetrics>} M
 */
export function drawJarBack(c, M) {
  var w = M.w, h = M.h;
  c.save();
  c.beginPath();
  innerU(c, M, true);
  c.fillStyle = JAR_GLASS.tint;
  c.fill();
  c.clip();
  // Shade crescent: the inside minus a copy shifted up-left (the toon kit's cel recipe, as a
  // cut-out so the translucent glass is not filled twice).
  var sx = clamp(w * 0.035, 5, 18);
  var sy = clamp(h * 0.025, 4, 14);
  c.beginPath();
  c.rect(-10, -10, w + 20, h + 20);
  c.save();
  c.translate(-sx, -sy);
  innerU(c, M, true);
  c.restore();
  c.fillStyle = JAR_GLASS.crescent;
  c.fill("evenodd");
  // Floor shade: a flat ellipse the creatures sit in.
  c.beginPath();
  c.ellipse(w * 0.5, h, w * 0.47, clamp(h * 0.045, 6, 26), 0, 0, TAU);
  c.fillStyle = JAR_GLASS.floor;
  c.fill();
  c.restore();
  // Back half of the round mouth: the far rim seen through the opening, the glass tint
  // continuing up to it.
  c.save();
  c.beginPath();
  c.ellipse(w / 2, 0, w / 2, M.mouth, 0, Math.PI, TAU);
  c.closePath();
  c.fillStyle = JAR_GLASS.tint;
  c.fill();
  c.beginPath();
  c.ellipse(w / 2, 0, w / 2 + M.wall / 2, M.mouth, 0, Math.PI, TAU);
  c.lineWidth = Math.max(1.5, M.wall * 0.42);
  c.strokeStyle = JAR_GLASS.rimBack;
  c.stroke();
  c.restore();
  c.save();
  c.beginPath();
  innerU(c, M, true);
  c.clip();
  // A few bubbles clinging to the glass low down.
  var br = clamp(w * 0.011, 2.2, 6);
  for (var i = 0; i < 6; i++) {
    var side = i % 2 === 0 ? 0 : 1;
    var bx = side ? w * (0.86 + 0.08 * hash(i * 3.1 + 1)) : w * (0.05 + 0.08 * hash(i * 5.7 + 2));
    var by = h * (0.5 + 0.42 * hash(i * 1.9 + 4));
    bubble(c, bx, by, br * (0.55 + 0.75 * hash(i * 7.3 + 6)));
  }
  c.restore();
}

/**
 * Over the creatures: glass walls (lit left, shaded right and floor), the inner edge, the
 * outer ink, the highlight bands across the glass and (unless `opts.noRim`) the mouth rim.
 * @param {CanvasRenderingContext2D} c jar-local transform
 * @param {ReturnType<typeof jarArtMetrics>} M
 * @param {{ noRim?: boolean }} [opts]
 */
export function drawJarFront(c, M, opts) {
  var w = M.w, h = M.h, t = M.wall;
  // 1. Wall glass, cel-shaded: base, a lit copy shifted up-left, a light patch on the left wall.
  c.save();
  wallBand(c, M);
  c.fillStyle = JAR_GLASS.wallShade;
  c.fill("evenodd");
  c.clip("evenodd");
  c.save();
  c.translate(-t * 0.45, -t * 0.5);
  wallBand(c, M);
  c.fillStyle = JAR_GLASS.wall;
  c.fill("evenodd");
  c.restore();
  c.fillStyle = JAR_GLASS.wallLight;
  c.fillRect(-t - 2, 0, t * 0.62 + 2, h - M.r * 0.6);
  // Glassy shine running down the left wall and along the floor.
  stripe(c, -t * 0.5, t * 1.4, -t * 0.5, h - M.r - t * 0.5, Math.max(1.2, t * 0.3), JAR_GLASS.stripe);
  stripe(c, M.r + t * 0.6, h + M.base * 0.45, M.r + Math.min(w * 0.16, t * 6), h + M.base * 0.45,
    Math.max(1, M.base * 0.28), "rgba(255,255,255,0.7)");
  c.restore();

  // 2. Highlight bands across the glass (they pass over the creatures, like a reflection):
  //    a wide band and a thin one on the lit left, a thin one on the right.
  var bw = clamp(w * 0.026, 4, 12);
  var bx = clamp(w * 0.045, 8, 24);
  stripe(c, bx, h * 0.08, bx, h * 0.6, bw, JAR_GLASS.band);
  stripe(c, bx, h * 0.67, bx, h * 0.7, bw, JAR_GLASS.band);
  var thin = Math.max(2, bw * 0.4);
  stripe(c, bx + bw * 1.35, h * 0.12, bx + bw * 1.35, h * 0.34, thin, JAR_GLASS.bandThin);
  var tx = w - clamp(w * 0.05, 9, 26);
  stripe(c, tx, h * 0.16, tx, h * 0.42, thin, JAR_GLASS.bandThin);
  stripe(c, tx, h * 0.47, tx, h * 0.5, thin, JAR_GLASS.bandThin);

  // 3. Inner edge: a thin line on the outside of the physics silhouette, so the glass reads
  //    as having a face without covering a body that touches it.
  var edge = Math.max(1.1, M.ink * 0.42);
  c.save();
  wallBand(c, M);
  c.clip("evenodd");
  c.beginPath();
  innerU(c, M, false);
  inkStyle(c, edge * 2, JAR_GLASS.innerEdge);
  c.stroke();
  c.restore();

  // 4. Outer ink: one bold line round the U.
  c.beginPath();
  outerU(c, M, false);
  inkStyle(c, M.ink, INK);
  c.stroke();
  // Close the wall tops.
  c.beginPath();
  c.moveTo(-t, 0);
  c.lineTo(0, 0);
  c.moveTo(w, 0);
  c.lineTo(w + t, 0);
  c.stroke();

  if (!(opts && opts.noRim)) drawJarRim(c, M);
}

/**
 * Front half of the round mouth (over the creatures), with an ink edge underneath it so the rim
 * reads as the near lip of a cylinder.
 * @param {CanvasRenderingContext2D} c jar-local transform
 * @param {ReturnType<typeof jarArtMetrics>} M
 */
export function drawJarRim(c, M) {
  var w = M.w, t = M.wall;
  c.save();
  c.beginPath();
  c.ellipse(w / 2, 0, w / 2 + t / 2, M.mouth, 0, 0, Math.PI);
  c.lineCap = "butt";
  c.lineWidth = Math.max(2, t * 0.42) + M.ink * 0.9;
  c.strokeStyle = JAR_GLASS.rimInk;
  c.stroke();
  c.lineWidth = Math.max(1.5, t * 0.42);
  c.strokeStyle = JAR_GLASS.rimFront;
  c.stroke();
  c.beginPath();
  c.ellipse(w / 2, 0, w / 2 + t / 2, M.mouth, 0, Math.PI * 0.62, Math.PI * 0.86);
  c.lineCap = "round";
  c.lineWidth = Math.max(1, t * 0.16);
  c.strokeStyle = "rgba(255,255,255,0.85)";
  c.stroke();
  c.restore();
}

function canBake() {
  if (typeof document === "undefined") return false;
  try {
    var probe = document.createElement("canvas");
    return !!(probe.getContext && probe.getContext("2d"));
  } catch (_e) {
    return false;
  }
}
var bakeable = null;

/** Jar-local box of the rim strip: the mouth's front half and its ink, full jar width. */
function rimBox(M) {
  var pad = Math.ceil(M.wall + M.ink + 2);
  return { left: M.left, right: M.right, top: -pad, bottom: Math.ceil(M.mouth) + pad };
}

function bakeLayer(M, res, paint, box) {
  var b = box || M;
  var cv = document.createElement("canvas");
  cv.width = Math.max(1, Math.ceil((b.right - b.left) * res));
  cv.height = Math.max(1, Math.ceil((b.bottom - b.top) * res));
  var c = cv.getContext("2d");
  if (!c) return null;
  c.scale(res, res);
  c.translate(-b.left, -b.top);
  c.lineJoin = "round";
  c.lineCap = "round";
  paint(c, M);
  // Drawn at its own size: no mip chain (render/pixi/sprite-pool.js).
  markTextureNoMipmaps(cv);
  return cv;
}

var cache = { key: "", layers: null };

/**
 * The baked jar for a size (memoised: a new size bakes new canvases and frees the old ones'
 * GPU textures, a canvas is never redrawn in place). Null without a 2D canvas.
 * @param {number} w inner width (px)
 * @param {number} h inner height (px)
 * @param {number} r inner corner radius (px)
 * @param {{ roomBelow?: number, resolution?: number }} [opts]
 * @returns {{ back: HTMLCanvasElement, front: HTMLCanvasElement, rim: HTMLCanvasElement, metrics: object, left: number, top: number, width: number, height: number, rimLeft: number, rimTop: number, rimWidth: number, rimHeight: number }|null}
 */
export function getJarLayers(w, h, r, opts) {
  var o = opts || {};
  if (bakeable == null) bakeable = canBake();
  if (!bakeable || !(w > 0) || !(h > 0)) return null;
  var res = clamp(o.resolution > 0 ? o.resolution : 1, 1, 2);
  var M = jarArtMetrics(w, h, r, o.roomBelow);
  var key = [w.toFixed(1), h.toFixed(1), M.r.toFixed(2), M.base.toFixed(2), res].join("|");
  if (cache.key === key && cache.layers) return cache.layers;
  var back = bakeLayer(M, res, drawJarBack);
  var front = bakeLayer(M, res, function (c) { drawJarFront(c, M, { noRim: true }); });
  var rb = rimBox(M);
  var rim = bakeLayer(M, res, drawJarRim, rb);
  if (!back || !front || !rim) return null;
  if (cache.layers) {
    releaseCanvasTexture(cache.layers.back);
    releaseCanvasTexture(cache.layers.front);
    releaseCanvasTexture(cache.layers.rim);
  }
  cache.key = key;
  cache.layers = {
    back: back,
    front: front,
    metrics: M,
    left: M.left,
    top: M.top,
    width: M.right - M.left,
    height: M.bottom - M.top,
    rim: rim,
    rimLeft: rb.left,
    rimTop: rb.top,
    rimWidth: rb.right - rb.left,
    rimHeight: rb.bottom - rb.top,
  };
  return cache.layers;
}

/** @internal Test helper. */
export function __resetJarArtForTests() {
  cache = { key: "", layers: null };
  bakeable = null;
}
